/**
 * OrderEditor — legacy-parity create/edit form for sales, purchases, quotations,
 * return orders and recurring sales (SaleFormComponent, PurchaseFormComponent,
 * QuotationFormComponent, ReturnOrderFormComponent, RecurringSaleFormComponent).
 * Wired through `config.customForm` in `crud/overrides/10-orders.ts`.
 *
 * CONTRACT (CrudCustomFormProps)
 *   recordId != null            → edit: GET {res}/{id}, mapped like legacy `formatRes`.
 *   prefill.duplicate_of = id   → load `GET {prefill.resource ?? res}/{id}` and clone it
 *                                 (ids, reference cleared; date = today; new draft).
 *   prefill.quotation_id = id   → load `GET quotations/{id}?attr=sale` and clone it into
 *                                 a new sale (legacy `/sales/add?quotation_id=`).
 *   any other prefill key       → applied to the header when it matches a header field
 *                                 (customer_id, supplier_id, type, reference, details,
 *                                 date, start_date, repeat) — used by `?create=1&…` links.
 *   Row actions (duplicate / convert) open it with `ctx.openCreate(prefill)`.
 *
 * Payload: `buildOrderPayload` (orderModel.ts) — the `items[]` shape of legacy
 * Form.js#submit; attachments switch the request to multipart automatically.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { CloseCircle, DocumentUpload, Edit2, SearchNormal1, Trash } from 'iconsax-reactjs';

import {
  adminApi,
  apiErrorMessage,
  apiValidationErrors,
  createRecord,
  type CustomFieldDefinition,
  fetchCustomFields,
  isLoyaltyEnabled,
  updateRecord
} from '../../api/client';
import { AttachmentsSection, CustomFieldInput } from '../../crud/CrudResourcePage';
import type { CrudCustomFormProps, CrudRecord } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { ensureLocation, useSessionLocation } from '../../session/location';
import { loadBootData } from '../documents/storeSettings';
import { LineItemDialog } from './LineItemDialog';
import {
  applicablePromotionIds,
  attachCatalog,
  buildOrderPayload,
  computeTotals,
  emptyOrderForm,
  lineFromCatalogItem,
  metaLabel,
  needsSelection,
  num,
  ORDER_KINDS,
  type OrderForm,
  type OrderKind,
  type OrderLine,
  type OrderResource,
  parseScaleBarcode,
  parseScaleSettings,
  partyOf,
  RECURRING_REPEATS,
  type ScaleBarcodeSettings,
  stateFromRecord,
  type Tax,
  toTax,
  validateOrderForm
} from './orderModel';
import { UI } from '../../brand/ui';

type PartyOption = { value: string; label: string; state?: string | null; customer_group?: { discount?: string | number | null } | null };

type EditorSettings = { maxDiscount: number; defaultCustomer: string; scale: ScaleBarcodeSettings | null; searchDelay: number };

const HEADER_PREFILL_KEYS = ['customer_id', 'supplier_id', 'type', 'reference', 'details', 'date', 'start_date', 'repeat'] as const;

function money(value: number) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function asParty(raw: unknown): PartyOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const value = String(r.value ?? r.id ?? '');
  if (!value) return null;
  const label = String(r.label ?? (r.company && r.name ? `${r.name} (${r.company})` : (r.name ?? r.company ?? value)));
  return {
    value,
    label,
    state: (r.state as string | null | undefined) ?? null,
    customer_group: (r.customer_group as PartyOption['customer_group']) ?? null
  };
}

function errorKeyLabel(key: string) {
  return key.replace(/^items\.(\d+)\./, (_m, i: string) => `${t('orders.line.item')} ${Number(i) + 1} · `).replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Remote party search (customers/search?q= | suppliers/search?q=)
// ---------------------------------------------------------------------------

function PartySelect({
  party,
  value,
  seed,
  error,
  onChange,
  onResolve
}: {
  party: 'customer' | 'supplier';
  value: string;
  seed: PartyOption[];
  error?: string;
  onChange: (option: PartyOption | null) => void;
  onResolve?: (option: PartyOption) => void;
}) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<PartyOption[]>(seed);
  const [loading, setLoading] = useState(false);
  const endpoint = party === 'customer' ? 'customers/search' : 'suppliers/search';

  useEffect(() => {
    setOptions((current) => {
      const merged = [...current];
      seed.forEach((s) => {
        if (!merged.some((o) => o.value === s.value)) merged.push(s);
      });
      return merged;
    });
  }, [seed]);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      adminApi
        .get(endpoint, { params: { q: input } })
        .then((response) => {
          if (!active) return;
          const rows = (Array.isArray(response.data) ? response.data : []).map(asParty).filter(Boolean) as PartyOption[];
          setOptions((current) => {
            const keep = current.filter((o) => o.value === value);
            const merged = [...rows];
            keep.forEach((k) => {
              if (!merged.some((o) => o.value === k.value)) merged.push(k);
            });
            return merged;
          });
        })
        .catch(() => undefined)
        .finally(() => active && setLoading(false));
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, endpoint]);

  // Resolve the label of a pre-selected party missing from the search page (default customer, deep links).
  useEffect(() => {
    if (!value || options.some((o) => o.value === value)) return;
    let active = true;
    adminApi
      .get(`${party === 'customer' ? 'customers' : 'suppliers'}/${value}`)
      .then((response) => {
        const option = asParty({ ...(response.data as object), value });
        if (active && option) setOptions((current) => (current.some((o) => o.value === option.value) ? current : [...current, option]));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, party]);

  const selected = options.find((o) => o.value === value) ?? null;
  // Keep the parent's party details (state for taxes, customer-group discount) in sync.
  useEffect(() => {
    if (selected) onResolve?.(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.value, selected?.state]);
  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      loadingText={t('common.loading')}
      noOptionsText={t('orders.noOptions')}
      filterOptions={(x) => x}
      onInputChange={(_e, next, reason) => {
        if (reason === 'input') setInput(next);
      }}
      onChange={(_e, option) => onChange(option)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionLabel={(o) => o.label}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label={party === 'customer' ? t('orders.field.customer') : t('orders.field.supplier')}
          placeholder={t('orders.typeToSearch')}
          error={Boolean(error)}
          helperText={error}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Item search / scan (items/search?q= | items_wp/search?type=standard,service&q=)
// ---------------------------------------------------------------------------

function ItemSearch({
  kind,
  settings,
  disabled,
  onPick,
  onScale
}: {
  kind: OrderKind;
  settings: EditorSettings;
  disabled?: boolean;
  onPick: (id: string) => Promise<void>;
  onScale: (item: Record<string, unknown>, qty: number, price: number | null) => void;
}) {
  const [input, setInput] = useState('');
  const [results, setResults] = useState<Array<{ id: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const clear = () => {
    setInput('');
    setResults([]);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    const query = input.trim();
    if (!query || query.length >= 35 || query.includes('(')) {
      setResults([]);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const scale = settings.scale;
        if (kind.field === 'price' && scale && num(scale.total_digits) > 0 && num(scale.total_digits) === query.length) {
          const parsed = parseScaleBarcode(query, scale);
          const response = await adminApi.get('items/search', { params: { scale: 1, q: parsed.item_code, barcode: query } });
          const data = response.data as Record<string, unknown> | Array<Record<string, unknown>>;
          if (data && !Array.isArray(data) && data.id) {
            const stock = (Array.isArray(data.location_stock) ? data.location_stock[0] : null) as Record<string, unknown> | null;
            const unitPrice = stock?.price ? num(stock.price) : num(data.price);
            let price: number | null = parsed.price ? Number(parsed.price.toFixed(4)) : null;
            let qty = parsed.weight ? Number(parsed.weight.toFixed(4)) : 0;
            if (!price && qty) price = Number((unitPrice / qty).toFixed(2));
            if (!qty && price) qty = Number((price / unitPrice).toFixed(4));
            if (active) {
              onScale(data, qty || 1, price);
              clear();
            }
            return;
          }
        }
        const response = await adminApi.get(kind.searchEndpoint(query));
        const rows = (Array.isArray(response.data) ? response.data : []) as Array<Record<string, unknown>>;
        if (!active) return;
        if (rows.length === 1) {
          await onPick(String(rows[0].id));
          clear();
          return;
        }
        setResults(rows.map((r) => ({ id: String(r.id), label: String(r.label ?? `${r.name} (${r.code})`) })));
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, settings.searchDelay);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={results}
      inputValue={input}
      filterOptions={(x) => x}
      loading={loading}
      loadingText={t('common.loading')}
      noOptionsText={t('orders.noOptions')}
      onInputChange={(_e, next, reason) => {
        if (reason !== 'reset') setInput(next);
      }}
      onChange={(_e, option) => {
        if (option && typeof option === 'object') {
          void onPick(option.id).then(clear);
        }
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          autoFocus
          placeholder={disabled ? t('orders.selectTypeFirst') : t('orders.searchScan')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, color: 'text.secondary' }}>
                {loading ? <CircularProgress size={16} /> : <SearchNormal1 size={18} />}
              </Box>
            )
          }}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export function OrderEditor({ config, recordId, prefill, onClose, onSaved }: CrudCustomFormProps) {
  const resource = (config.apiResource ?? config.resource) as OrderResource;
  const kind = ORDER_KINDS[resource] ?? ORDER_KINDS.sales;
  const location = useSessionLocation();
  const locationState = (location.locations.find((l) => l.value === location.currentId)?.state as string | null | undefined) ?? null;

  const [form, setForm] = useState<OrderForm>(() => emptyOrderForm(kind));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [settings, setSettings] = useState<EditorSettings>({ maxDiscount: 0, defaultCustomer: '', scale: null, searchDelay: 250 });
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [partySeed, setPartySeed] = useState<PartyOption[]>([]);
  const [selectedParty, setSelectedParty] = useState<PartyOption | null>(null);
  const [attachments, setAttachments] = useState<unknown[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [lineDialog, setLineDialog] = useState<{ line: OrderLine; mode: 'add' | 'edit' } | null>(null);
  const [busyLine, setBusyLine] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const initialRef = useRef<OrderForm | null>(null);

  const editing = Boolean(form.id);
  const party = partyOf(form, kind);
  const taxCtx = useMemo(() => ({ partyState: selectedParty?.state ?? null, locationState }), [selectedParty, locationState]);
  const totals = useMemo(() => computeTotals(form, kind, taxes, taxCtx), [form, kind, taxes, taxCtx]);

  const setField = <K extends keyof OrderForm>(key: K, value: OrderForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  // ---- loading -------------------------------------------------------------

  const applyRecord = useCallback(
    (record: Record<string, unknown>, sourceKind: OrderKind, duplicate: boolean) => {
      const state = stateFromRecord(record, sourceKind, duplicate);
      // A clone from another resource (quotation → sale) takes the target defaults:
      // its type and, for draft-capable orders, draft = true (never auto-post).
      const next: OrderForm =
        sourceKind.resource !== kind.resource ? { ...state, type: kind.defaultType, draft: kind.hasDraft, wasDraft: true } : state;
      const seeds = [
        asParty(record.customer && { ...(record.customer as object), value: record.customer_id }),
        asParty(record.supplier && { ...(record.supplier as object), value: record.supplier_id })
      ].filter(Boolean) as PartyOption[];
      setPartySeed(seeds);
      setSelectedParty(seeds.find((s) => s.value === (partyOf(next, kind) === 'supplier' ? next.supplier_id : next.customer_id)) ?? null);
      setAttachments(duplicate ? [] : Array.isArray(record.attachments) ? record.attachments : []);
      setForm(next);
      return next;
    },
    [kind]
  );

  const loadRecord = useCallback(
    async (id: string | number) => {
      const response = await adminApi.get(`${resource}/${id}`);
      return applyRecord(response.data as Record<string, unknown>, kind, false);
    },
    [applyRecord, kind, resource]
  );

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setNotice(null);
      try {
        await ensureLocation().catch(() => undefined);
        const [boot, taxResponse, defs] = await Promise.all([
          loadBootData(),
          adminApi.get('taxes/search').catch(() => ({ data: [] })),
          fetchCustomFields(kind.customFieldResource)
        ]);
        if (!active) return;
        const s = boot.settings ?? {};
        const nextSettings: EditorSettings = {
          maxDiscount: num(s.max_discount),
          defaultCustomer: String(s.default_customer ?? ''),
          scale: parseScaleSettings(s.scale_barcode),
          searchDelay: num(s.search_delay, 250) || 250
        };
        setSettings(nextSettings);
        setTaxes((Array.isArray(taxResponse.data) ? taxResponse.data : []).map((tax: Record<string, unknown>) => toTax(tax)));
        setCustomFieldDefs(defs);

        let state: OrderForm;
        if (recordId != null) {
          state = await loadRecord(recordId);
        } else if (prefill?.duplicate_of) {
          const source = (String(prefill.resource ?? resource) as OrderResource) || resource;
          const response = await adminApi.get(`${source}/${prefill.duplicate_of}`);
          state = applyRecord(response.data as Record<string, unknown>, ORDER_KINDS[source] ?? kind, true);
        } else if (prefill?.quotation_id) {
          const response = await adminApi.get(`quotations/${prefill.quotation_id}`, { params: { attr: 'sale' } });
          state = applyRecord(response.data as Record<string, unknown>, ORDER_KINDS.quotations, true);
        } else {
          state = emptyOrderForm(kind, { customerId: nextSettings.defaultCustomer });
          setForm(state);
        }
        // Header pre-fill from deep links (?create=1&customer_id=…).
        if (prefill && recordId == null) {
          const patch: Partial<OrderForm> = {};
          HEADER_PREFILL_KEYS.forEach((key) => {
            if (prefill[key] != null && prefill[key] !== '') (patch as Record<string, unknown>)[key] = String(prefill[key]);
          });
          if (Object.keys(patch).length) {
            state = { ...state, ...patch };
            setForm(state);
          }
        }
        initialRef.current = recordId == null && !prefill?.duplicate_of && !prefill?.quotation_id ? state : null;
      } catch (err) {
        if (active) setNotice(apiErrorMessage(err, t('common.unableToLoad', { name: tLabel(config.singular) })));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, resource]);

  // ---- lines ---------------------------------------------------------------

  const groupDiscount = kind.field === 'price' ? num(selectedParty?.customer_group?.discount) : 0;

  const refreshPromotions = (lines: OrderLine[]) => {
    if (kind.field !== 'price') return lines;
    const total = lines.reduce((a, l) => a + num(l.price) * num(l.quantity), 0);
    return lines.map((l) => (l.allPromotions.length ? { ...l, promotions: applicablePromotionIds(l, total) } : l));
  };

  const commitNewLine = (line: OrderLine) => {
    setForm((current) => {
      const plain =
        !line.selectedVariations.length && !line.selectedPortions.length && !line.selectedModifiers.length && !line.serials.length;
      const existing = plain
        ? current.items.find(
            (l) =>
              l.item_id === line.item_id &&
              !l.selectedVariations.length &&
              !l.selectedPortions.length &&
              !l.selectedModifiers.length &&
              !l.serials.length &&
              l.unit_id === line.unit_id
          )
        : null;
      let items: OrderLine[];
      if (existing) {
        items = current.items.map((l) => (l.key === existing.key ? { ...l, quantity: num(l.quantity) + num(line.quantity) } : l));
        setToast(t('orders.itemUpdated', { item: line.name }));
      } else {
        items = [
          ...current.items,
          {
            ...line,
            discount:
              current.discount_method === 'items' && num(current.discount) > 0 && !line.discount ? num(current.discount) : line.discount
          }
        ];
        setToast(t('orders.itemAdded', { item: line.name }));
      }
      return { ...current, items: refreshPromotions(items) };
    });
    setFieldErrors((current) => {
      const next = { ...current };
      delete next.items;
      return next;
    });
  };

  const addCatalogItem = (raw: Record<string, unknown>, options: { quantity?: number; price?: number } = {}) => {
    const line = lineFromCatalogItem(raw, kind, { ...options, groupDiscount });
    if (needsSelection(line, kind) && !(line.type === 'recipe' && line.selectedPortions.length)) {
      setLineDialog({ line, mode: 'add' });
      return;
    }
    commitNewLine(line);
  };

  const pickItem = async (id: string) => {
    setBusyLine(true);
    try {
      const response = await adminApi.get(kind.resolveEndpoint(id));
      addCatalogItem(response.data as Record<string, unknown>);
    } catch (err) {
      setNotice(apiErrorMessage(err, t('orders.error.itemLoad')));
    } finally {
      setBusyLine(false);
    }
  };

  const openLine = async (line: OrderLine) => {
    if (line.catalogLoaded) {
      setLineDialog({ line, mode: 'edit' });
      return;
    }
    setBusyLine(true);
    try {
      const response = await adminApi.get(kind.resolveEndpoint(line.item_id));
      const enriched = attachCatalog(line, response.data as Record<string, unknown>, kind);
      setForm((current) => ({ ...current, items: current.items.map((l) => (l.key === line.key ? enriched : l)) }));
      setLineDialog({ line: enriched, mode: 'edit' });
    } catch {
      setLineDialog({ line: { ...line, catalogLoaded: true }, mode: 'edit' });
    } finally {
      setBusyLine(false);
    }
  };

  const saveLine = (line: OrderLine) => {
    if (lineDialog?.mode === 'add') commitNewLine(line);
    else setForm((current) => ({ ...current, items: refreshPromotions(current.items.map((l) => (l.key === line.key ? line : l))) }));
    setLineDialog(null);
  };

  const removeLine = (key: string) => {
    setForm((current) => ({ ...current, items: current.items.filter((l) => l.key !== key) }));
    setLineDialog(null);
  };

  const setLineQuantity = (key: string, quantity: number) =>
    setForm((current) => ({ ...current, items: refreshPromotions(current.items.map((l) => (l.key === key ? { ...l, quantity } : l))) }));

  // legacy `form.discount` / `form.discount_method` watchers.
  const applyOrderDiscount = (discount: string, method: OrderForm['discount_method']) => {
    setForm((current) => {
      const value = num(discount);
      let items = current.items;
      if (method === 'items') items = current.items.map((l) => ({ ...l, discount: value > 0 ? value : null }));
      else if (current.discount_method !== 'order') items = current.items.map((l) => ({ ...l, discount: null }));
      return { ...current, discount, discount_method: method, items };
    });
  };

  const changeParty = (option: PartyOption | null) => {
    const previous = num(selectedParty?.customer_group?.discount);
    const nextGroup = num(option?.customer_group?.discount);
    setSelectedParty(option);
    setForm((current) => {
      const items =
        kind.field === 'price' && (previous || nextGroup)
          ? current.items.map((l) => {
              const value = num(l.discount) - previous + nextGroup;
              return { ...l, discount: value > 0 ? value : null };
            })
          : current.items;
      return party === 'supplier'
        ? { ...current, supplier_id: option?.value ?? '', items }
        : { ...current, customer_id: option?.value ?? '', items };
    });
    setFieldErrors((current) => {
      const next = { ...current };
      delete next.customer_id;
      delete next.supplier_id;
      return next;
    });
  };

  // ---- save ----------------------------------------------------------------

  const save = async (stay: boolean) => {
    setNotice(null);
    setServerErrors({});
    const errors = validateOrderForm(form, kind, settings.maxDiscount);
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setNotice(t('orders.error.invalidForm'));
      return;
    }
    const posting = kind.hasDraft && !form.draft;
    if (resource === 'sales' && posting && (await isLoyaltyEnabled())) {
      setNotice(t('orders.error.loyaltyPosting'));
      return;
    }
    setSaving(true);
    try {
      await ensureLocation();
      const payload = buildOrderPayload(form, kind, {
        taxes,
        ctx: taxCtx,
        customFieldSlugs: customFieldDefs.map((d) => d.slug),
        attachments: files
      });
      const response = form.id
        ? await updateRecord<CrudRecord>(resource, form.id, payload)
        : await createRecord<CrudRecord>(resource, payload);
      if (response && response.success === false) throw new Error(response.message || t('common.actionFailed'));
      setFiles([]);
      if (stay) {
        setToast(form.id ? t('orders.updated', { name: tLabel(config.singular) }) : t('orders.created', { name: tLabel(config.singular) }));
        if (form.id) {
          await loadRecord(form.id);
        } else {
          const reset = emptyOrderForm(kind, { customerId: settings.defaultCustomer });
          setForm(initialRef.current ? { ...initialRef.current, items: [], reference: '', details: '', extra_attributes: {} } : reset);
          setAttachments([]);
        }
        return;
      }
      onSaved(response?.data as CrudRecord | undefined);
    } catch (err) {
      const validation = apiValidationErrors(err);
      setServerErrors(validation);
      const mapped: Record<string, string> = {};
      Object.entries(validation).forEach(([key, messages]) => {
        if (!key.startsWith('items.')) mapped[key] = messages[0];
      });
      setFieldErrors(mapped);
      setNotice(apiErrorMessage(err, t('common.actionFailed')));
    } finally {
      setSaving(false);
    }
  };

  // ---- render --------------------------------------------------------------

  const fieldError = (key: string) =>
    fieldErrors[key]
      ? fieldErrors[key].startsWith('orders.')
        ? t(fieldErrors[key], { percent: settings.maxDiscount })
        : fieldErrors[key]
      : undefined;
  const typeLabel = (value: string) => t(`orders.type.${value}`);
  const title = editing
    ? t('orders.title.edit', { name: tLabel(config.singular) })
    : t('orders.title.create', { name: tLabel(config.singular) });
  const itemsDisabled = kind.party === 'byType' && !form.type;
  const showDraft = kind.hasDraft && (!editing || form.wasDraft);
  const taxOptions = taxes.map((tax) => ({ value: tax.id, label: `${tax.name} (${num(tax.rate)}%)` }));

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      onClose={() => !saving && onClose()}
      PaperProps={{ sx: { bgcolor: UI.surface, backgroundImage: 'none' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          bgcolor: UI.white
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: UI.ink }}>
            {title}
          </Typography>
          {editing && form.reference ? (
            <Typography variant="body2" color="text.secondary">
              {form.reference}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>
            {t('action.cancel')}
          </Button>
          <Button variant="outlined" disabled={saving || loading} onClick={() => void save(true)}>
            {t('orders.saveStay')}
          </Button>
          <Button variant="contained" disabled={saving || loading} onClick={() => void save(false)} sx={{ minWidth: 110 }}>
            {saving ? t('action.saving') : t('action.save')}
          </Button>
          <IconButton aria-label={t('action.cancel')} onClick={onClose} disabled={saving}>
            <CloseCircle size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: UI.surface, py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ maxWidth: 1280, mx: 'auto', pt: 1 }}>
            {notice ? (
              <Alert severity="error" onClose={() => setNotice(null)}>
                {notice}
                {Object.keys(serverErrors).length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                    {Object.entries(serverErrors)
                      .slice(0, 10)
                      .map(([key, messages]) => (
                        <li key={key}>
                          <strong>{errorKeyLabel(key)}</strong>: {messages[0]}
                        </li>
                      ))}
                  </Box>
                ) : null}
              </Alert>
            ) : null}

            {/* Header */}
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2.5, border: `1px solid ${UI.border}` }}>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <Stack spacing={2}>
                  {kind.typeOptions ? (
                    <TextField
                      select
                      required
                      label={t('orders.field.type')}
                      value={form.type}
                      error={Boolean(fieldError('type'))}
                      helperText={fieldError('type')}
                      onChange={(e) => setField('type', e.target.value)}
                      disabled={kind.party === 'byType' && editing}
                    >
                      {kind.typeOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          {typeLabel(value)}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                  {kind.recurring ? (
                    <>
                      <TextField
                        required
                        type="date"
                        label={t('orders.field.startDate')}
                        value={form.start_date}
                        onChange={(e) => setField('start_date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldError('start_date'))}
                        helperText={fieldError('start_date')}
                      />
                      <TextField
                        select
                        required
                        label={t('orders.field.repeat')}
                        value={form.repeat}
                        onChange={(e) => setField('repeat', e.target.value)}
                        error={Boolean(fieldError('repeat'))}
                        helperText={fieldError('repeat')}
                      >
                        {RECURRING_REPEATS.map((value) => (
                          <MenuItem key={value} value={value}>
                            {t(`orders.repeat.${value}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </>
                  ) : (
                    <TextField
                      required
                      type="date"
                      label={t('orders.field.date')}
                      value={form.date}
                      onChange={(e) => setField('date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldError('date'))}
                      helperText={fieldError('date')}
                    />
                  )}
                  {kind.party !== 'byType' || form.type ? (
                    <PartySelect
                      key={party}
                      party={party}
                      value={party === 'supplier' ? form.supplier_id : form.customer_id}
                      seed={partySeed}
                      error={fieldError(party === 'supplier' ? 'supplier_id' : 'customer_id')}
                      onChange={changeParty}
                      onResolve={(option) =>
                        setSelectedParty((current) =>
                          current?.value === option.value && current.state === option.state ? current : option
                        )
                      }
                    />
                  ) : null}
                  {kind.recurring ? (
                    <TextField
                      type="number"
                      label={t('orders.field.createBefore')}
                      helperText={t('orders.field.createBeforeHelp')}
                      value={form.create_before}
                      onChange={(e) => setField('create_before', e.target.value)}
                      inputProps={{ min: 0 }}
                    />
                  ) : null}
                  <TextField
                    label={t('orders.field.reference')}
                    value={form.reference}
                    onChange={(e) => setField('reference', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                    error={Boolean(fieldError('reference'))}
                    helperText={fieldError('reference')}
                  />
                </Stack>
                <Stack spacing={2}>
                  <Autocomplete
                    multiple
                    noOptionsText={t('orders.noOptions')}
                    options={taxOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    value={taxOptions.filter((o) => form.taxes.includes(o.value))}
                    onChange={(_e, value) =>
                      setField(
                        'taxes',
                        value.map((v) => v.value)
                      )
                    }
                    renderInput={(params) => <TextField {...params} label={t('orders.field.orderTaxes')} />}
                  />
                  {kind.hasShipping ? (
                    <TextField
                      type="number"
                      label={t('orders.field.shipping')}
                      value={form.shipping}
                      onChange={(e) => setField('shipping', e.target.value)}
                      inputProps={{ step: 'any', min: 0 }}
                      error={Boolean(fieldError('shipping'))}
                      helperText={fieldError('shipping')}
                    />
                  ) : null}
                  {settings.maxDiscount > 0 ? (
                    <Box>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('orders.field.discount')}
                        value={form.discount}
                        onChange={(e) => applyOrderDiscount(e.target.value, form.discount_method)}
                        inputProps={{ min: 0, max: settings.maxDiscount, step: 'any' }}
                        error={Boolean(fieldError('discount'))}
                        helperText={fieldError('discount') ?? t('orders.field.discountHelp', { percent: settings.maxDiscount })}
                      />
                      <RadioGroup
                        row
                        value={form.discount_method}
                        onChange={(e) => applyOrderDiscount(form.discount, e.target.value as OrderForm['discount_method'])}
                      >
                        <FormControlLabel value="order" control={<Radio size="small" />} label={t('orders.field.discountOrder')} />
                        <FormControlLabel value="items" control={<Radio size="small" />} label={t('orders.field.discountItems')} />
                      </RadioGroup>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            </Box>

            {/* Items */}
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2.5, border: `1px solid ${UI.border}` }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <ItemSearch
                      kind={kind}
                      settings={settings}
                      disabled={itemsDisabled}
                      onPick={pickItem}
                      onScale={(raw, qty, price) => addCatalogItem(raw, { quantity: qty, price: price ?? undefined })}
                    />
                  </Box>
                  {busyLine ? <CircularProgress size={20} /> : null}
                </Stack>
                {fieldError('items') ? <Alert severity="warning">{fieldError('items')}</Alert> : null}
                {!form.items.length ? (
                  <Alert severity="info" variant="outlined">
                    <strong>{t('orders.emptyOrder')}</strong> — {t('orders.emptyOrderText')}
                  </Alert>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 820 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>{t('orders.col.description')}</TableCell>
                          <TableCell align="right">{kind.field === 'price' ? t('orders.line.price') : t('orders.line.cost')}</TableCell>
                          <TableCell align="right">{t('orders.line.discount')}</TableCell>
                          <TableCell align="right" sx={{ width: 120 }}>
                            {t('orders.line.quantity')}
                          </TableCell>
                          <TableCell align="right">{t('orders.line.taxes')}</TableCell>
                          <TableCell align="right">{t('orders.col.subtotal')}</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.items.map((line, index) => {
                          const lt = totals.lines[line.key];
                          const composite = line.selectedVariations.length > 0 || line.selectedPortions.length > 0;
                          return (
                            <TableRow key={line.key} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {line.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {line.code}
                                  {line.units.length > 1 && line.unit_id
                                    ? ` · ${line.units.find((u) => u.id === line.unit_id)?.name ?? ''}`
                                    : ''}
                                  {line.batch_no ? ` · ${t('orders.line.batchNo')}: ${line.batch_no}` : ''}
                                  {line.expiry_date ? ` · ${line.expiry_date}` : ''}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                  {line.selectedVariations.map((v) => (
                                    <Chip key={v.id} size="small" variant="outlined" label={`${metaLabel(v.meta)} × ${num(v.quantity)}`} />
                                  ))}
                                  {line.selectedPortions.map((p, i) => (
                                    <Chip
                                      key={`${p.id}-${i}`}
                                      size="small"
                                      variant="outlined"
                                      label={`${p.name === 'regular' ? t('orders.line.regular') : p.name} × ${num(p.quantity)}`}
                                    />
                                  ))}
                                  {line.selectedModifiers.map((m) => (
                                    <Chip
                                      key={`${m.mId}-${m.id}`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      label={`${m.title ? `${m.title}: ` : ''}${m.option}`}
                                    />
                                  ))}
                                  {line.serials.length ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${t('orders.line.serials')}: ${
                                        line.serialOptions
                                          .filter((s) => line.serials.includes(s.id))
                                          .map((s) => s.number)
                                          .join(', ') || line.serials.length
                                      }`}
                                    />
                                  ) : null}
                                  {line.promotions.length ? (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label={line.allPromotions
                                        .filter((p) => line.promotions.includes(p.id))
                                        .map((p) => p.name)
                                        .join(', ')}
                                    />
                                  ) : null}
                                </Stack>
                                {line.comment ? (
                                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }} color="text.secondary">
                                    {line.comment}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell align="right">{money(lt?.value ?? num(line[kind.field]))}</TableCell>
                              <TableCell align="right">
                                {form.discount_method === 'order' || !line.discount ? '—' : `${num(line.discount)}%`}
                                {lt?.discount_amount ? (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {money(lt.discount_amount)}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell align="right">
                                {composite ? (
                                  num(line.quantity) ||
                                  (line.selectedVariations.length ? line.selectedVariations : line.selectedPortions).reduce(
                                    (a, s) => a + num(s.quantity),
                                    0
                                  )
                                ) : (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={line.quantity}
                                    onChange={(e) => setLineQuantity(line.key, num(e.target.value))}
                                    inputProps={{
                                      min: 0,
                                      step: 'any',
                                      style: { textAlign: 'right' },
                                      'aria-label': t('orders.line.quantity')
                                    }}
                                    sx={{ width: 100 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right">{money(lt?.total_tax_amount ?? 0)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {money(lt?.subtotal ?? 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip title={t('orders.line.editTooltip')}>
                                  <IconButton size="small" onClick={() => void openLine(line)} aria-label={t('orders.line.editTooltip')}>
                                    <Edit2 size={16} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('orders.line.remove')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeLine(line.key)}
                                    aria-label={t('orders.line.remove')}
                                  >
                                    <Trash size={16} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {/* Totals bar (legacy OrderHelpers computed values) */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
                    bgcolor: UI.warningTint,
                    border: `1px solid ${UI.warningLine}`,
                    borderRadius: 1.5,
                    p: 1.5
                  }}
                >
                  <TotalCell
                    label={t('orders.totals.items')}
                    value={`${totals.items} · ${t('orders.totals.qty')} ${num(totals.quantity)}`}
                  />
                  <TotalCell label={t('orders.totals.discount')} value={money(totals.item_discount_amount + totals.discount_amount)} />
                  <TotalCell label={t('orders.totals.tax')} value={money(totals.total_tax_amount)} />
                  <TotalCell label={t('orders.totals.shipping')} value={money(totals.shipping)} />
                  <TotalCell label={t('orders.totals.payable')} value={money(totals.grand_total)} strong />
                </Box>
              </Stack>
            </Box>

            {/* Custom fields */}
            {customFieldDefs.length ? (
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2.5, border: `1px solid ${UI.border}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: UI.ink, mb: 2 }}>
                  {t('crud.customFields')}
                </Typography>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  {customFieldDefs.map((definition) => (
                    <Box key={definition.slug} sx={{ gridColumn: definition.type === 'textarea' ? '1 / -1' : undefined }}>
                      <CustomFieldInput
                        definition={definition}
                        value={form.extra_attributes[definition.slug]}
                        error={serverErrors[`extra_attributes.${definition.slug}`]?.[0]}
                        onChange={(value) => setField('extra_attributes', { ...form.extra_attributes, [definition.slug]: value })}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}

            {/* Attachments, details, flags */}
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2.5, border: `1px solid ${UI.border}` }}>
              <Stack spacing={2}>
                {editing && attachments.length ? (
                  <AttachmentsSection
                    record={{ id: form.id as string, attachments } as CrudRecord}
                    canDelete
                    onDeleted={() => {
                      if (!form.id) return;
                      void adminApi
                        .get(`${resource}/${form.id}`)
                        .then((r) => setAttachments(Array.isArray(r.data?.attachments) ? r.data.attachments : []));
                    }}
                    onError={(message) => setNotice(message)}
                  />
                ) : null}
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf,.zip"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files ?? []);
                      setFiles((current) => [...current, ...picked]);
                      e.target.value = '';
                    }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DocumentUpload size={16} />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('orders.attachFiles')}
                    </Button>
                    {files.map((file, i) => (
                      <Chip
                        key={`${file.name}-${i}`}
                        size="small"
                        label={file.name}
                        onDelete={() => setFiles((current) => current.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </Stack>
                  {serverErrors.attachments || Object.keys(serverErrors).some((k) => k.startsWith('attachments.')) ? (
                    <Typography variant="caption" color="error">
                      {(serverErrors.attachments ?? Object.entries(serverErrors).find(([k]) => k.startsWith('attachments.'))?.[1] ?? [])[0]}
                    </Typography>
                  ) : null}
                </Box>
                <TextField
                  label={t('orders.field.details')}
                  multiline
                  minRows={2}
                  value={form.details}
                  onChange={(e) => setField('details', e.target.value)}
                />
                <Divider />
                {showDraft ? (
                  <FormControlLabel
                    control={<Checkbox checked={form.draft} onChange={(e) => setField('draft', e.target.checked)} />}
                    label={kind.recurring ? t('orders.draftRecurring') : t('orders.draftText')}
                  />
                ) : null}
                {kind.hasDraft && !form.draft ? (
                  <Alert severity="warning" variant="outlined">
                    {kind.recurring ? t('orders.postingRecurringWarning') : t('orders.postingWarning')}
                  </Alert>
                ) : null}
                {kind.hasDeductFromRegister && !editing ? (
                  <FormControlLabel
                    control={
                      <Checkbox checked={form.deduct_from_register} onChange={(e) => setField('deduct_from_register', e.target.checked)} />
                    }
                    label={t('orders.deductFromRegister')}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      {lineDialog ? (
        <LineItemDialog
          key={lineDialog.line.key}
          line={lineDialog.line}
          kind={kind}
          taxes={taxes}
          mode={lineDialog.mode}
          onCancel={() => setLineDialog(null)}
          onSave={saveLine}
          onRemove={lineDialog.mode === 'edit' ? () => removeLine(lineDialog.line.key) : undefined}
        />
      ) : null}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity="success" variant="filled" onClose={() => setToast(null)}>
            {toast}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
}

function TotalCell({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant={strong ? 'subtitle1' : 'body2'} sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default OrderEditor;
